import { FC } from 'react';
import type { DreLinha, DreMes, TipoLinhaDre } from '@/types/dashboards';
import { fmtMil } from '@/utils/equiplexDre';
import { fmtPct, mesCurto } from '@/utils/painelFormat';
import PainelCard from '../PainelCard';

export type ModoDre = 'valor' | 'pct';

interface Props {
  linhas: DreLinha[];
  meses: DreMes[];
  mes: string;
  modo: ModoDre;
  onModo: (m: ModoDre) => void;
}

const ESTILO_LINHA: Record<TipoLinhaDre, string> = {
  linha: 'text-content-secondary',
  subtotal: 'font-semibold text-content-primary border-t border-theme-border',
  resultado: 'font-bold text-content-primary border-t-2 border-theme-border',
  fora: 'italic text-content-muted',
  memo: 'italic text-content-muted border-t border-dashed border-theme-border',
  indicador: 'font-semibold text-content-primary',
};

const ANOS = (meses: DreMes[]) => Array.from(new Set(meses.map((m) => m.key.slice(0, 4))));

function celula(l: DreLinha, chave: string, modo: ModoDre): string {
  return modo === 'pct' ? fmtPct(l.pct_rl[chave] ?? null) : fmtMil(l.valores[chave]);
}

/** No modo %, o acumulado é a razão dos acumulados do ano, não a média das razões mensais. */
function acumulado(l: DreLinha, ano: string, modo: ModoDre, rlAno: Record<string, number>): string {
  if (modo === 'valor') return fmtMil(l.acumulado[ano]);
  const rl = rlAno[ano];
  return rl ? fmtPct((l.acumulado[ano] / rl) * 100) : '—';
}

const SeletorModo: FC<{ modo: ModoDre; onModo: (m: ModoDre) => void }> = ({ modo, onModo }) => (
  <div role="group" aria-label="Unidade" className="inline-flex rounded-lg border border-theme-border p-0.5 text-xs">
    {(
      [
        ['valor', 'R$ mil'],
        ['pct', '% receita líquida'],
      ] as const
    ).map(([id, rotulo]) => (
      <button
        key={id}
        type="button"
        aria-pressed={modo === id}
        onClick={() => onModo(id)}
        className={`rounded-md px-3 py-1.5 ${
          modo === id ? 'bg-surface-hover font-medium text-content-primary' : 'text-content-muted hover:text-content-primary'
        }`}
      >
        {rotulo}
      </button>
    ))}
  </div>
);

/**
 * A DRE mês a mês na estrutura do Datasul, com o acumulado do ano. O mês escolhido fica
 * destacado; mês com o contábil ainda incompleto leva um asterisco no cabeçalho.
 */
const DreTable: FC<Props> = ({ linhas, meses, mes, modo, onModo }) => {
  const anos = ANOS(meses);
  const rlAno = linhas.find((l) => l.key === 'receita_liquida')?.acumulado ?? {};
  return (
    <PainelCard
      testId="dre-tabela"
      titulo="Demonstração do resultado"
      subtitulo={modo === 'valor' ? 'Valores em R$ mil. Receita positiva, custo e despesa negativos.' : 'Cada linha em % da receita líquida do mês.'}
      acoes={<SeletorModo modo={modo} onModo={onModo} />}
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm tabular-nums">
          <thead>
            <tr className="border-b border-theme-border text-xs uppercase tracking-wide text-content-muted">
              <th className="sticky left-0 bg-surface-card py-2 pr-3 text-left font-medium">Linha</th>
              {meses.map((m) => (
                <th
                  key={m.key}
                  scope="col"
                  className={`px-2 py-2 text-right font-medium ${m.key === mes ? 'text-content-primary' : ''}`}
                  title={m.contabil_incompleto ? 'Contábil do mês ainda incompleto' : m.label}
                >
                  {mesCurto(m.label)}
                  {m.contabil_incompleto && '*'}
                </th>
              ))}
              {anos.map((a) => (
                <th
                  key={a}
                  scope="col"
                  title={`Soma dos meses de ${a} que estão na tabela`}
                  className="border-l border-theme-border px-2 py-2 text-right font-medium"
                >
                  {a}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.key} data-testid={`dre-linha-${l.key}`} className={ESTILO_LINHA[l.tipo]}>
                <th scope="row" className="sticky left-0 bg-surface-card py-1.5 pr-3 text-left font-[inherit]">
                  {l.rotulo}
                </th>
                {meses.map((m) => (
                  <td key={m.key} className={`px-2 py-1.5 text-right ${m.key === mes ? 'bg-surface-hover' : ''}`}>
                    {celula(l, m.key, modo)}
                  </td>
                ))}
                {anos.map((a) => (
                  <td key={a} className="border-l border-theme-border px-2 py-1.5 text-right">
                    {acumulado(l, a, modo, rlAno)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {meses.some((m) => m.contabil_incompleto) && (
        <p className="text-xs text-content-muted">* Mês com a receita contábil abaixo da receita das notas fiscais: o contábil ainda está sendo lançado.</p>
      )}
    </PainelCard>
  );
};

export default DreTable;
